import React, { useState } from 'react';
import './MonthlyBreakdown.css';

interface MonthlyBreakdownProps {
  detailedData: any[];
  inflationView?: 'real' | 'nominal';
  applyInflation?: (value: number, year: number) => number;
}

const MonthlyBreakdown: React.FC<MonthlyBreakdownProps> = ({
  detailedData,
  inflationView = 'real',
  applyInflation = (val) => val
}) => {
  const [selectedYear, setSelectedYear] = useState(detailedData[0]?.year || 2024);

  if (!detailedData || detailedData.length === 0) {
    return <div className="monthly-breakdown">No data available</div>;
  }

  // Find the selected year's data
  const yearData = detailedData.find(d => d.year === selectedYear) || detailedData[0];

  // Calculate monthly amounts (divide annual by 12) and apply inflation
  const monthlyIncome = {
    cpp: applyInflation((yearData.cpp_income || 0) / 12, selectedYear),
    oas: applyInflation((yearData.oas_final || 0) / 12, selectedYear),
    pension: applyInflation((yearData.pension_income || 0) / 12, selectedYear),
    rrsp: applyInflation((yearData.rrsp_withdrawal || 0) / 12, selectedYear),
    tfsa: applyInflation((yearData.tfsa_withdrawal || 0) / 12, selectedYear),
    nonReg: applyInflation((yearData.non_registered_withdrawal || 0) / 12, selectedYear),
  };

  const monthlySpending = applyInflation((yearData.annual_spending || 0) / 12, selectedYear);
  const monthlyTaxes = applyInflation((yearData.taxes || 0) / 12, selectedYear);
  const totalMonthlyIncome = Object.values(monthlyIncome).reduce((a, b) => a + b, 0);
  const netMonthlyCashFlow = totalMonthlyIncome - monthlySpending - monthlyTaxes;

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(val);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className="monthly-breakdown">
      <div className="monthly-header">
        <div>
          <h3>Monthly Cash Flow Breakdown</h3>
          <p style={{fontSize: '12px', color: '#666', margin: '4px 0 0 0'}}>
            Viewing in <strong>{inflationView === 'real' ? "today's dollars" : "future inflated dollars"}</strong>
          </p>
        </div>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="year-selector"
        >
          {detailedData.map(d => (
            <option key={d.year} value={d.year}>
              {d.year} (Age {d.age})
            </option>
          ))}
        </select>
      </div>

      <div className="monthly-summary-cards">
        <div className="summary-card income">
          <div className="card-label">Monthly Income</div>
          <div className="card-value">{formatCurrency(totalMonthlyIncome)}</div>
        </div>
        <div className="summary-card spending">
          <div className="card-label">Monthly Spending</div>
          <div className="card-value">{formatCurrency(monthlySpending)}</div>
        </div>
        <div className="summary-card taxes">
          <div className="card-label">Monthly Taxes</div>
          <div className="card-value">{formatCurrency(monthlyTaxes)}</div>
        </div>
        <div className={`summary-card net ${netMonthlyCashFlow >= 0 ? 'positive' : 'negative'}`}>
          <div className="card-label">Net Cash Flow</div>
          <div className="card-value">{formatCurrency(netMonthlyCashFlow)}</div>
        </div>
      </div>

      <div className="income-breakdown">
        <h4>Income Sources</h4>
        <div className="income-grid">
          {monthlyIncome.cpp > 0 && (
            <div className="income-item">
              <span className="income-label">CPP</span>
              <span className="income-amount">{formatCurrency(monthlyIncome.cpp)}</span>
            </div>
          )}
          {monthlyIncome.oas > 0 && (
            <div className="income-item">
              <span className="income-label">OAS</span>
              <span className="income-amount">{formatCurrency(monthlyIncome.oas)}</span>
            </div>
          )}
          {monthlyIncome.pension > 0 && (
            <div className="income-item">
              <span className="income-label">Pension</span>
              <span className="income-amount">{formatCurrency(monthlyIncome.pension)}</span>
            </div>
          )}
          {monthlyIncome.rrsp > 0 && (
            <div className="income-item">
              <span className="income-label">RRSP Withdrawals</span>
              <span className="income-amount">{formatCurrency(monthlyIncome.rrsp)}</span>
            </div>
          )}
          {monthlyIncome.tfsa > 0 && (
            <div className="income-item">
              <span className="income-label">TFSA Withdrawals</span>
              <span className="income-amount">{formatCurrency(monthlyIncome.tfsa)}</span>
            </div>
          )}
          {monthlyIncome.nonReg > 0 && (
            <div className="income-item">
              <span className="income-label">Non-Registered Withdrawals</span>
              <span className="income-amount">{formatCurrency(monthlyIncome.nonReg)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="monthly-calendar">
        <h4>Calendar View</h4>
        <div className="calendar-grid">
          {months.map(month => (
            <div key={month} className="calendar-month">
              <div className="month-name">{month}</div>
              <div className="month-income">{formatCurrency(totalMonthlyIncome)}</div>
              <div className="month-spending">-{formatCurrency(monthlySpending + monthlyTaxes)}</div>
              <div className={`month-net ${netMonthlyCashFlow >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(netMonthlyCashFlow)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="monthly-note">
        💡 Monthly amounts are calculated by dividing annual projections by 12.
        {inflationView === 'nominal' && ' Values shown reflect future inflated dollars.'}
        {inflationView === 'real' && ' Values shown in today\'s dollars (inflation-adjusted).'}
        {' '}Actual monthly income may vary based on payment schedules (CPP/OAS are monthly, RRSP withdrawals can be customized).
      </p>
    </div>
  );
};

export default MonthlyBreakdown;
